/**
 * Client-side analytics helpers (Phase 15).
 * - useSessionId: stable UUID per browser session (sessionStorage)
 * - usePageTracking: fires POST /api/track/pageview on every route change
 * - trackCartEvent: fire-and-forget helper used by CartContext
 *
 * All tracking calls are non-blocking and silently swallow errors so they
 * never degrade the user experience.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = 'odw_session_id';

function genSessionId() {
  // RFC4122-ish v4; crypto when available, Math.random fallback
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'sid-' + Date.now().toString(36) + '-' +
    Math.random().toString(36).slice(2, 10);
}

const isDev = process.env.NODE_ENV === 'development';
const logIfDev = (e) => { if (isDev && e) console.debug('[analytics]', e); };

export function getSessionId() {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = genSessionId();
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch (e) {
    // sessionStorage blocked (private mode, etc.) — use in-memory fallback
    logIfDev(e);
    if (!window.__odw_mem_sid) window.__odw_mem_sid = genSessionId();
    return window.__odw_mem_sid;
  }
}

export function PageTracker() {
  const location = useLocation();
  useEffect(() => {
    // Skip admin pages from public traffic stats — admin activity shouldn't
    // pollute customer-facing analytics.
    if (location.pathname.startsWith('/admin')) return;
    const payload = {
      path: location.pathname,
      referrer: document.referrer || null,
      session_id: getSessionId(),
    };
    axios
      .post(`${API_URL}/api/track/pageview`, payload, {
        // Tracking must never throw — no credentials needed, no toast on error
        withCredentials: false,
      })
      .catch(logIfDev);
  }, [location.pathname]);
  return null;
}

export function trackCartEvent(productId, action = 'add', quantity = 1) {
  const payload = {
    product_id: String(productId || ''),
    action,
    quantity: Number(quantity) || 1,
    session_id: getSessionId(),
  };
  axios
    .post(`${API_URL}/api/track/cart-event`, payload, { withCredentials: false })
    .catch(logIfDev);
}
